import React, { useEffect, useRef, useState } from 'react';
import { api, errText } from '@/lib/api';
import { CodePicker } from '@/components/CodePicker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Plus, Upload, Download, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

const BLANK = { name: '', icd10: '', cpt: '', severity_variants: { Mild: { recovery_weeks: 4, restrictions: {} }, Moderate: { recovery_weeks: 6, restrictions: {} }, Severe: { recovery_weeks: 12, restrictions: {} } }, follow_up_cadence: '', incapacity_type: 'Continuous', ai_notes: '' };

export default function AdminCatalog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get('/catalog'), api.get('/catalog/import-history')]);
    setItems(a.data || []);
    setHistory(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing({ ...BLANK }); setOpen(true); };
  const startEdit = (it) => { setEditing(JSON.parse(JSON.stringify(it))); setOpen(true); };
  const remove = async (id) => { if (!window.confirm('Delete this procedure?')) return; await api.delete(`/catalog/${id}`); toast.success('Deleted'); load(); };

  const save = async () => {
    if (!editing.name) { toast.error('Name required'); return; }
    let r;
    try {
      r = editing.id ? await api.patch(`/catalog/${editing.id}`, editing) : await api.post('/catalog', editing);
    } catch (err) { toast.error(errText(err)); return; }
    const warnings = r.data.code_warnings || [];
    if (warnings.length) toast.warning(editing.id ? 'Updated — check the codes' : 'Created — check the codes', { description: warnings.map(w => w.warning).join(' ') });
    else toast.success(editing.id ? 'Updated' : 'Created');
    setOpen(false);
    load();
  };

  const downloadTemplate = async () => {
    const r = await api.get('/catalog/csv-template');
    const blob = new Blob([r.data.csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = r.data.filename; a.click(); URL.revokeObjectURL(url);
  };

  const onCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };

  const doImport = async () => {
    if (!csvText.trim()) { toast.error('CSV required'); return; }
    const r = await api.post('/catalog/import', { csv: csvText });
    const failedWhy = (r.data.failed || []).slice(0, 3).map(f => `Row ${f.row}: ${f.reason}`);
    const warned = (r.data.code_warnings || []).slice(0, 3).map(w => `Row ${w.row}: ${w.warning}`);
    toast[failedWhy.length || warned.length ? 'warning' : 'success'](
      `Import: ${r.data.added} added, ${r.data.updated} updated, ${r.data.failed_count} failed`,
      { description: [...failedWhy, ...warned].join(' ') || undefined });
    setImportOpen(false); setCsvText('');
    load();
  };

  const setVariant = (sev, key, value) => {
    setEditing(e => ({ ...e, severity_variants: { ...e.severity_variants, [sev]: { ...(e.severity_variants?.[sev] || {}), [key]: value } } }));
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Procedure Catalog</h1>
        <span className="text-xs text-muted-foreground">{items.length} entries</span>
        <div className="flex-1" />
        <Button variant="outline" onClick={downloadTemplate} data-testid="admin-csv-template-button"><Download className="h-4 w-4 mr-1" /> CSV template</Button>
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="admin-procedure-csv-import-button"><Upload className="h-4 w-4 mr-1" /> Import CSV</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Import procedure catalog (CSV)</DialogTitle><DialogDescription>Upload a CSV file or paste rows. Existing names are updated; new names are added.</DialogDescription></DialogHeader>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => onCsvFile(e.target.files?.[0])} data-testid="admin-csv-file-input" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> Choose .csv file</Button>
              <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={10} placeholder="Or paste CSV contents here..." data-testid="admin-csv-text-area" />
              <p className="text-xs text-muted-foreground">Headers: name,icd10,cpt,mild_recovery_weeks,moderate_recovery_weeks,severe_recovery_weeks,follow_up_cadence,incapacity_type,ai_notes</p>
            </div>
            <DialogFooter><Button onClick={doImport} data-testid="admin-csv-import-confirm">Import</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Button onClick={startCreate} data-testid="admin-add-procedure-button"><Plus className="h-4 w-4 mr-1" /> Add procedure</Button>
      </div>
      {/* Phone: card list */}
      <div className="md:hidden space-y-3" data-testid="admin-catalog-cards">
        {items.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">No procedures yet.</Card>}
        {items.map((it) => {
          const sv = it.severity_variants || {};
          return (
            <Card key={it.id} className="p-4 space-y-2" data-testid={`admin-catalog-card-${it.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{it.name}</p>
                <p className="text-xs text-muted-foreground truncate">{[it.icd10, it.cpt].filter(Boolean).join(' · ') || '—'}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Mild / Mod / Severe: {sv.Mild?.recovery_weeks ?? '—'} / {sv.Moderate?.recovery_weeks ?? '—'} / {sv.Severe?.recovery_weeks ?? '—'} wk
                {it.follow_up_cadence ? ` · ${it.follow_up_cadence}` : ''}
              </p>
              <div className="flex gap-2 pt-1">
                <Button className="flex-1" variant="outline" onClick={() => startEdit(it)} data-testid={`admin-catalog-edit-card-${it.id}`}><Edit className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                <Button className="flex-1" variant="outline" onClick={() => remove(it.id)} data-testid={`admin-catalog-delete-card-${it.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tablet and up: table */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-catalog-table">
            <thead className="bg-card border-b">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">ICD-10</th>
                <th className="px-3 py-2">CPT</th>
                <th className="px-3 py-2">Mild / Mod / Severe (weeks)</th>
                <th className="px-3 py-2">Follow-up</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b hover:bg-muted/60" data-testid={`admin-catalog-row-${it.id}`}>
                  <td className="px-3 py-2 font-medium">{it.name}</td>
                  <td className="px-3 py-2">{it.icd10}</td>
                  <td className="px-3 py-2">{it.cpt}</td>
                  <td className="px-3 py-2">{(it.severity_variants || {}).Mild?.recovery_weeks ?? '—'} / {(it.severity_variants || {}).Moderate?.recovery_weeks ?? '—'} / {(it.severity_variants || {}).Severe?.recovery_weeks ?? '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{it.follow_up_cadence}</td>
                  <td className="px-3 py-2"><div className="flex gap-1"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(it)}><Edit className="h-3.5 w-3.5" /></Button><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(it.id)}><Trash2 className="h-3.5 w-3.5" /></Button></div></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No procedures yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {history.length > 0 && (
        <Card className="mt-4">
          <CardHeader><CardTitle className="text-base">Import history</CardTitle></CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              {history.slice(0, 5).map(h => (
                <li key={h.id} className="flex justify-between"><span className="text-muted-foreground">{(h.imported_at || '').slice(0, 16).replace('T', ' ')} · {h.imported_by}</span><span>{h.added} added · {h.updated} updated · {h.failed_count} failed</span></li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit procedure' : 'Add procedure'}</DialogTitle><DialogDescription>Maintain practice-specific recovery templates that ground the AI output.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2"><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing(s => ({ ...s, name: e.target.value }))} data-testid="admin-procedure-name" /></div>
                <div><Label>ICD-10</Label><CodePicker kind="icd10" value={editing.icd10} onChange={(v) => setEditing(s => ({ ...s, icd10: v }))} placeholder="Code or words" testid="catalog-icd10" align="right" /></div>
                <div><Label>CPT</Label><CodePicker kind="procedure" value={editing.cpt} onChange={(v) => setEditing(s => ({ ...s, cpt: v }))} placeholder="e.g. 29881" testid="catalog-cpt" /></div>
                <div><Label>Incapacity type</Label><Input value={editing.incapacity_type} onChange={(e) => setEditing(s => ({ ...s, incapacity_type: e.target.value }))} /></div>
                <div><Label>Follow-up cadence</Label><Input value={editing.follow_up_cadence} onChange={(e) => setEditing(s => ({ ...s, follow_up_cadence: e.target.value }))} /></div>
                <div className="md:col-span-3"><Label>AI notes (used as grounding context)</Label><Textarea rows={2} value={editing.ai_notes} onChange={(e) => setEditing(s => ({ ...s, ai_notes: e.target.value }))} /></div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs font-medium text-muted-foreground mb-2">Severity variants (recovery weeks)</div>
                <div className="grid grid-cols-3 gap-2">
                  {['Mild', 'Moderate', 'Severe'].map(sev => (
                    <div key={sev}>
                      <Label>{sev}</Label>
                      <Input type="number" value={editing.severity_variants?.[sev]?.recovery_weeks ?? ''} onChange={(e) => setVariant(sev, 'recovery_weeks', parseInt(e.target.value || '0'))} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="admin-procedure-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
